import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useContextMenu } from 'app/components/ChakraTableList';
import { Representative } from 'types/profile';
import { useAppDispatch } from 'app/store';
import { deleteRepresentativeEffect } from 'app/store/Profile/effects';
import { Row } from '@tanstack/react-table';
import { StickyTable } from 'app/components/Core/StickyTable/StickyTable';
import { contextMenuHandler } from 'app/components/Table/ContextMenuButton/utils';
import { useTotalWidth } from 'app/components/Core/StickyTable';
import { RepresentativeDialog } from '../RepresentativeDialog';
import { DeleteRepresentativeDialog } from '../DeleteRepresentativeDialog';
import { RepresentativesTableRow } from './RepresentativesTableRow';
import { getColumns } from './utils';

export interface RepresentativesListComponentProps {
  representatives: Representative[];
  loading: boolean;
}

export const RepresentativesListComponent: FC<RepresentativesListComponentProps> = ({
  representatives = [],
  loading,
}) => {
  const disaptch = useAppDispatch();
  const [editRepresentative, setEditRepresentative] = useState<{
    index: number;
    representative: Representative;
  } | null>(null);

  const [
    deleteRepresentativeIndex,
    setDeleteRepresentativeIndex,
  ] = useState<number>(-1);

  const ref = useRef<HTMLDivElement>(null);
  const tableKey = 'profile-representatives';
  const totalWidth = useTotalWidth(ref);
  const columns = useMemo(() => getColumns(totalWidth), [totalWidth]);
  const contextMenuProps = useContextMenu();

  const rowRenderer = useCallback(
    (row: Row<Representative>, index: number) => {
      const representative = row.original;
      const options = [
        {
          title: 'Edit',
          value: 'EDIT',
          onClick: () => setEditRepresentative({ index, representative }),
        },
        {
          title: 'Delete',
          value: 'DELETE',
          onClick: () => setDeleteRepresentativeIndex(index),
        },
      ];

      return (
        <RepresentativesTableRow
          key={row.id}
          row={row}
          contextHandler={(e) =>
            contextMenuHandler(e, contextMenuProps, options)
          }
        />
      );
    },
    [contextMenuProps]
  );

  // TODO need to find better solution
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => setIsLoading(loading), [loading]);

  return (
    <>
      <StickyTable<Representative>
        tableKey={tableKey}
        ref={ref}
        data={representatives}
        columns={columns}
        rowRenderer={rowRenderer}
        contextMenuProps={contextMenuProps}
        loading={isLoading}
        data-testid="Representatives-list"
      />
      <RepresentativeDialog
        open={!!editRepresentative}
        represantative={editRepresentative?.representative}
        index={editRepresentative?.index}
        onClose={() => setEditRepresentative(null)}
      />
      <DeleteRepresentativeDialog
        open={deleteRepresentativeIndex > -1}
        onClose={() => setDeleteRepresentativeIndex(-1)}
        handleAction={() => {
          disaptch(deleteRepresentativeEffect(deleteRepresentativeIndex));
          setDeleteRepresentativeIndex(-1);
        }}
      />
    </>
  );
};
